import { getIncidentSeverityDefaultValue } from '@features/logs/utils/incidentSeverityUtils'
import {
  PERIOD_TIME_KEY,
  type PeriodSeries,
  periodSeriesToRows
} from '@features/reports/utils/periodSeriesToRows'
import { BarChart, LineChart, PieChart } from '@lmc/design-system'
import {
  type Home,
  type IncidentSeverityValues,
  IncidentStatus,
  TimeSeriesOptions
} from '@lmc/ts-common'
import { Select, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Col, Row } from 'antd'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { capitalize } from 'lodash'
import { forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useIncidentsReportList } from '../../../hooks'
import type { FilterValues } from '../../../hooks/useIncidentsReportList'
import { IncidentCard } from './IncidentCard'

// Highcharts' default first-series colour, which this chart had before; no token matches it.
const ACTIONS_COLOUR = '#2caffe'

type StatusCount = {
  name: IncidentStatus
  y: number
}

const CASES_COLOURS: Record<IncidentStatus, string> = {
  [IncidentStatus.closed]: '#0FCA7A',
  [IncidentStatus.needsReview]: '#F75D5F',
  [IncidentStatus.open]: '#FBC62F'
}

const TIME_SERIES_COLOURS = [
  '#46A182',
  '#004c6d',
  '#ff8c00',
  '#7851a9',
  '#F75D5F',
  '#708090'
]

// 'Not set', then severities 1 to 5.
const SEVERITY_COLOURS = [
  theme.color.neutral.color4,
  '#1AF2E5',
  '#F1E85D',
  '#FEA700',
  '#FC7411',
  '#F75D5F'
]

type IncidentChartProps = {
  filterParams: FilterValues
  testId?: string
}

export const IncidentsCharts = forwardRef<HTMLDivElement, IncidentChartProps>(
  ({ filterParams, testId = 'incidents-charts' }, ref) => {
    const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

    const [incidentDropdownValue, setIncidentDropdownValue] =
      useState<TimeSeriesOptions>(TimeSeriesOptions.total)

    const { data } = useIncidentsReportList(filterParams, 'dashboard')
    const incidents = data?.data ?? []

    const handleChange = (value: TimeSeriesOptions) => {
      setIncidentDropdownValue(value)
    }

    const home = useSelector(
      (state: { global: { home: Home } }) => state.global.home
    )

    const homeSeverities = fillSeverityMissingKeys(home.incidentSeverity)

    const mappedSeverities = Object.fromEntries(
      Object.entries(homeSeverities ?? {}).map(([key, value]) => {
        return [
          key.toString(),
          value || getIncidentSeverityDefaultValue(parseInt(key, 10), t)
        ]
      })
    )

    const mapSeverities = (data: PeriodSeries[]) => {
      return data.map((item, index) => ({
        ...item,
        name: mappedSeverities[index] ?? item.name
      }))
    }

    const mappedLineChartData = () => {
      return incidentDropdownValue === TimeSeriesOptions.severity
        ? mapSeverities(incidents.timeSeries[incidentDropdownValue].data)
        : incidents.timeSeries[incidentDropdownValue].data
    }

    // One point per week from the range start, as the dashboard API buckets them.
    const timeSeriesChart = periodSeriesToRows(
      mappedLineChartData(),
      incidents.timeSeries.startDate,
      'week'
    )

    const topIncidentSeries: PeriodSeries[] = incidents?.horizontalBarChart
      ? mapSeverities(incidents.horizontalBarChart.data)
      : []

    const incidentsOptions = [
      {
        value: TimeSeriesOptions.total,
        label: t(incidentReportsConstants[TimeSeriesOptions.total])
      },
      {
        value: TimeSeriesOptions.location,
        label: t(incidentReportsConstants[TimeSeriesOptions.location])
      },
      {
        value: TimeSeriesOptions.incidentType,
        label: t(incidentReportsConstants[TimeSeriesOptions.incidentType])
      },
      {
        value: TimeSeriesOptions.severity,
        label: t(incidentReportsConstants[TimeSeriesOptions.severity])
      }
    ]

    return (
      <ChartsContainer ref={ref} data-testid={testId}>
        <TopChartsContainer data-testid={`${testId}-top-charts-container`}>
          <IncidentCardWrapper data-testid={`${testId}-actions-card-wrapper`}>
            <IncidentCardContainer
              data-testid={`${testId}-actions-card-container`}
            >
              <IncidentCard
                data-testid={`${testId}-actions-card`}
                title={t(incidentReportsConstants.ACTIONS)}
                headerAction={
                  <StyledLink
                    to="/reports/incidents/actions"
                    data-testid={`${testId}-view-action-table-link`}
                  >
                    {t(incidentReportsConstants.VIEW_ACTION_TABLE)}
                  </StyledLink>
                }
              >
                <div data-testid={`${testId}-actions-column-chart`}>
                  <BarChart
                    aria-label={t(incidentReportsConstants.ACTIONS)}
                    categoryKey="action"
                    data={incidents.actions.categories.map(
                      (name: string, index: number) => ({
                        action: capitalize(t(name)),
                        count: incidents.actions.data[index]
                      })
                    )}
                    emptyMessage={t(incidentReportsConstants.NO_DATA)}
                    height={230}
                    series={[
                      {
                        colour: ACTIONS_COLOUR,
                        key: 'count',
                        label: t(incidentReportsConstants.ACTIONS)
                      }
                    ]}
                  />
                </div>
              </IncidentCard>
            </IncidentCardContainer>
          </IncidentCardWrapper>
          <TopRightContainer data-testid={`${testId}-top-right-container`}>
            <IncidentCard
              data-testid={`${testId}-cases-card`}
              title={t(incidentReportsConstants.CASES)}
            >
              <div data-testid={`${testId}-cases-donut-chart`}>
                <PieChart
                  aria-label={t(incidentReportsConstants.CASES)}
                  data={incidents.donutChart.data.map((item: StatusCount) => ({
                    colour: CASES_COLOURS[item.name],
                    name: capitalize(t(item.name)),
                    value: item.y
                  }))}
                  emptyMessage={t(incidentReportsConstants.NO_DATA)}
                  labels="inside"
                />
              </div>
            </IncidentCard>
            <IncidentCard
              data-testid={`${testId}-cases-filled-card`}
              title={
                <div data-testid={`${testId}-cases-filled-title`}>
                  {t(incidentReportsConstants.CASES)}
                  <br />
                  {t(incidentReportsConstants.FILLED_OUT)}
                </div>
              }
              subtitle={t(incidentReportsConstants.FILLING_CASE_DETAILS)}
            >
              <CaseContainer data-testid={`${testId}-case-container`}>
                <CaseTypeContainer
                  data-testid={`${testId}-debriefing-container`}
                >
                  <Text data-testid={`${testId}-debriefing-label`}>
                    {t(incidentReportsConstants.DEBRIEFING)}
                  </Text>
                  <PercentageText
                    data-testid={`${testId}-debriefing-percentage`}
                  >
                    {incidents?.casesFilled?.debriefing}%
                  </PercentageText>
                </CaseTypeContainer>
                <CaseTypeContainer
                  data-testid={`${testId}-root-cause-container`}
                >
                  <Text data-testid={`${testId}-root-cause-label`}>
                    {t(incidentReportsConstants.ROOT_CAUSE_PCT)}
                  </Text>
                  <PercentageText
                    data-testid={`${testId}-root-cause-percentage`}
                  >
                    {incidents?.casesFilled?.rootCause}%
                  </PercentageText>
                </CaseTypeContainer>
              </CaseContainer>
            </IncidentCard>
          </TopRightContainer>
        </TopChartsContainer>
        <RowContainer
          gutter={24}
          style={{ minHeight: '450px' }}
          data-testid={`${testId}-line-chart-row`}
        >
          <ColContainer span={24} data-testid={`${testId}-line-chart-col`}>
            <IncidentCard
              data-testid={`${testId}-incidents-time-card`}
              title={t(incidentReportsConstants.INCIDENTS_TIME)}
              headerAction={
                <Select
                  data-testid={`${testId}-time-series-select`}
                  style={{ width: 150 }}
                  onChange={handleChange}
                  options={incidentsOptions}
                  value={incidentDropdownValue}
                />
              }
            >
              <div data-testid={`${testId}-incidents-time-line-chart`}>
                <LineChart
                  aria-label={t(incidentReportsConstants.INCIDENTS_TIME)}
                  data={timeSeriesChart.rows}
                  emptyMessage={t(incidentReportsConstants.NO_DATA)}
                  height={350}
                  legend={
                    incidentDropdownValue === TimeSeriesOptions.total
                      ? 'none'
                      : 'bottom'
                  }
                  series={timeSeriesChart.series.map((series, index) => ({
                    ...series,
                    colour: TIME_SERIES_COLOURS[index]
                  }))}
                  xAxis={{
                    domain: [
                      dayjs(incidents.timeSeries.startDate).valueOf(),
                      dayjs(incidents.timeSeries.endDate).valueOf()
                    ],
                    title: t(incidentReportsConstants.TIME)
                  }}
                  xKey={PERIOD_TIME_KEY}
                  yAxis={{ title: t(incidentReportsConstants.TOTAL_INCIDENTS) }}
                />
              </div>
            </IncidentCard>
          </ColContainer>
        </RowContainer>
        <RowContainer
          gutter={24}
          style={{ minHeight: '488px' }}
          data-testid={`${testId}-horizontal-bar-chart-row`}
        >
          <ColContainer
            span={24}
            data-testid={`${testId}-horizontal-bar-chart-col`}
          >
            <IncidentCard
              data-testid={`${testId}-top-incidents-card`}
              title={t(incidentReportsConstants.TOP_INCIDENTS)}
            >
              {incidents?.horizontalBarChart && (
                <div
                  data-testid={`${testId}-top-incidents-horizontal-bar-chart`}
                >
                  <BarChart
                    aria-label={t(incidentReportsConstants.TOP_INCIDENTS)}
                    categoryAxis={{
                      title: t(incidentReportsConstants.TYPE_OF_INCIDENT)
                    }}
                    categoryKey="type"
                    data={incidents.horizontalBarChart.categories.map(
                      (type: string, index: number) => ({
                        type,
                        ...Object.fromEntries(
                          topIncidentSeries.map(({ data }, seriesIndex) => [
                            `s${seriesIndex}`,
                            data[index]
                          ])
                        )
                      })
                    )}
                    emptyMessage={t(incidentReportsConstants.NO_DATA)}
                    height={450}
                    orientation="horizontal"
                    series={topIncidentSeries.map(({ name }, index) => ({
                      colour: SEVERITY_COLOURS[index],
                      key: `s${index}`,
                      label: name
                    }))}
                    stacking="normal"
                    tooltipHideZero
                    valueAxis={{
                      title: t(incidentReportsConstants.TOTAL_INCIDENTS)
                    }}
                  />
                </div>
              )}
            </IncidentCard>
          </ColContainer>
        </RowContainer>
      </ChartsContainer>
    )
  }
)

const ChartsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const TopChartsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 1 0;
  gap: 24px;
  width: 100%;
  ${({ theme }) => theme.mediaQueries?.tabletDown} {
    flex-direction: column;
  }
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    flex: 0 1 auto;
    flex-direction: column;
    gap: 24px;
  }
`

const TopRightContainer = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: row;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    flex-direction: column;
    `

const ColContainer = styled(Col)`
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    min-width: 555px;
  }
`

const RowContainer = styled(Row)`
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    overflow: scroll;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const IncidentCardContainer = styled.div`
  height: 100%;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    min-width: 555px;
  }
`

const IncidentCardWrapper = styled.div`
  width: 50%;
  ${({ theme }) => theme.mediaQueries?.tabletDown} {
    width: 100%;
  }
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    overflow: scroll;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const CaseContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    margin-top: 24px;
  }
`

const PercentageText = styled(Text)`
  font-weight: ${(props) => props.theme.font.weight.bold};
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    font-size: 16px;
  }
`

const CaseTypeContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const StyledLink = styled(Link)`
  text-decoration: underline;
  font-weight: ${({ theme }) => theme.font.weight.medium};
`

function fillSeverityMissingKeys(
  obj?: IncidentSeverityValues,
  start: number = 1,
  end: number = 5
): IncidentSeverityValues {
  const result = {
    1: undefined,
    2: undefined,
    3: undefined,
    4: undefined,
    5: undefined
  } as IncidentSeverityValues

  for (let i = start; i <= end; i += 1) {
    const key = i as keyof IncidentSeverityValues
    result[key] = obj?.[key]
  }

  return result
}
